import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { PageHeader } from "@/business-settings/common/page-header";
import { useBusiness } from "@/core/business";
import { useDebounce } from "@/core/hooks";
import { cn } from "@/core/utils/utils";
import {
  SPIFF_RULE_EVENTS,
  SPIFF_RULE_EVENTS_OPTIONS,
} from "@/spiff-rules/constants";
import { useGetLatestSpiffRules } from "@/spiff-rules/query";
import { SpiffRuleEvent } from "@/spiff-rules/spiff-rule";
import { getOrderedSpiffRuleEvents } from "@/spiff-rules/utils";
import { PostOnboardingItems } from "@/waiting-pages";
import { ActivityEventModal } from "./activity-event-modal";
import { ActivityFeedHeader } from "./activity-feed-header";
import { ActivityFeedHelpCard } from "./activity-feed-help-card";
import type { IEnrichedActivityFeedItem } from "./activity-feed.d";
import { ActivityItem } from "./activity-item";
import { ActivityItemSkeleton } from "./activity-item-skeleton";
import { getSpiffEventName } from "./constants";
import {
  useGetActivityFeed,
  useGetActivityFeedEvents,
  useGetActivityFeedHasUnattributedReviews,
} from "./query";
import { UnattributedReviewsFilterCard } from "./unattributed-reviews-filter-card";

const ActivityFeed = () => {
  const { businessId } = useBusiness();
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isHeaderAtFullWidth, setIsHeaderAtFullWidth] = useState(false);
  const [selectedActivity, setSelectedActivity] =
    useState<IEnrichedActivityFeedItem | null>(null);
  const [showUnattributedReviews, setShowUnattributedReviews] = useState(false);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const {
    data: activityFeedPages,
    isLoading,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetActivityFeed(businessId, {
    selectedFilters,
    search: debouncedSearchValue,
    showUnattributedReviews,
  });
  const { data: hasUnattributedReviews = false } =
    useGetActivityFeedHasUnattributedReviews(businessId);
  const { data: activityFeedEvents = [] } =
    useGetActivityFeedEvents(businessId);
  const { data: spiffRules = [] } = useGetLatestSpiffRules(businessId);

  const activities = useMemo(
    () => activityFeedPages?.pages.flatMap((page) => page.items) ?? [],
    [activityFeedPages],
  );

  const getActivityKey = useCallback(
    (activity: IEnrichedActivityFeedItem) =>
      `${activity.event_name}-${activity.underlying_record_type}-${activity.underlying_record_id}-${activity.timestamp}`,
    [],
  );

  const availableEventTypes = useMemo(() => {
    const types = activityFeedEvents
      .map((eventOption) => getSpiffEventName(eventOption.event_name))
      .filter(
        (eventName): eventName is SpiffRuleEvent =>
          eventName in SPIFF_RULE_EVENTS_OPTIONS,
      );

    return getOrderedSpiffRuleEvents(types);
  }, [activityFeedEvents]);

  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (viewport) {
      viewport.scrollTop = 0;
    }
  }, [debouncedSearchValue, selectedFilters, showUnattributedReviews]);

  const skeletonItems = useMemo(
    () =>
      Array.from({ length: 4 }, (_, i) => (
        <ActivityItemSkeleton key={`skeleton-${i}`} />
      )),
    [],
  );

  const handleOpenEventModal = useCallback(
    (activity: IEnrichedActivityFeedItem) => {
      setSelectedActivity(activity);
    },
    [],
  );

  const handleCloseEventModal = useCallback(() => {
    setSelectedActivity(null);
  }, []);

  const handleToggleUnattributedReviews = useCallback(() => {
    setShowUnattributedReviews((prev) => !prev);
    // Clear other filters when showing unattributed reviews
    if (!showUnattributedReviews) {
      setSelectedFilters([]);
      setSearchValue("");
    }
  }, [showUnattributedReviews]);

  const handleLoadNextPage = useCallback(() => {
    if (!hasNextPage || isFetching || isFetchingNextPage || isLoading) {
      return;
    }

    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading]);

  const hasActiveServerFilters =
    showUnattributedReviews ||
    selectedFilters.length > 0 ||
    debouncedSearchValue.trim().length > 0;
  const hasLoadedFeedData = !!activityFeedPages;
  const shouldShowUnattributedReviewFilterCard =
    hasUnattributedReviews || showUnattributedReviews;
  const showInitialLoadingState = isPending && !hasLoadedFeedData;
  const shouldSuppressEmptyState = isFetching && hasLoadedFeedData;
  const isActivitiesEmpty =
    activities.length === 0 &&
    !isLoading &&
    !hasActiveServerFilters &&
    !shouldSuppressEmptyState;
  const showNoActivitiesMatchFilters =
    activities.length === 0 &&
    hasActiveServerFilters &&
    !isLoading &&
    !shouldSuppressEmptyState;
  const showInlineLoadingSkeleton =
    activities.length === 0 && shouldSuppressEmptyState;

  const tglRule = spiffRules.find(
    (rule) => rule.event === SPIFF_RULE_EVENTS.tech_generated_lead,
  );

  if (showInitialLoadingState) {
    return (
      <Stack gap="lg" fullWidth className="h-[calc(100vh-64px)]">
        <div className="mx-auto w-full max-w-[1456px] px-8 pt-4">
          <Stack
            direction="row"
            align="center"
            justify="between"
            fullWidth
            className="h-[44px]"
          >
            <PageHeader>Activity Feed</PageHeader>
          </Stack>
        </div>
        <ScrollArea className="h-full w-full">
          <Stack className="items-center p-8 pt-0" gap="md">
            {skeletonItems}
          </Stack>
        </ScrollArea>
      </Stack>
    );
  }

  if (isActivitiesEmpty) {
    return (
      <Stack gap="lg" fullWidth className="h-[calc(100vh-64px)]">
        <div className="mx-auto w-full max-w-[1456px] px-8 pt-4">
          <Stack
            direction="row"
            align="center"
            justify="between"
            fullWidth
            className="h-[44px]"
          >
            <PageHeader>Activity Feed</PageHeader>
          </Stack>
        </div>
        <ScrollArea className="h-full w-full">
          <Stack className="items-center justify-center p-8 pt-0" gap="md">
            <PostOnboardingItems />
          </Stack>
        </ScrollArea>
      </Stack>
    );
  }

  return (
    <div className="relative h-[calc(100vh-64px)] w-full">
      <ScrollArea className="h-full w-full" viewportRef={scrollViewportRef}>
        <ActivityFeedHeader
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          selectedFilters={selectedFilters}
          onFilterChange={setSelectedFilters}
          availableEventTypes={availableEventTypes}
          isPopoverOpen={isPopoverOpen}
          onPopoverOpenChange={setIsPopoverOpen}
          onFullWidthChange={setIsHeaderAtFullWidth}
          showUnattributedReviews={showUnattributedReviews}
          onToggleUnattributedReviews={handleToggleUnattributedReviews}
          hasUnattributedReviews={shouldShowUnattributedReviewFilterCard}
          showSupportCards={hasLoadedFeedData}
        />

        <Stack
          className={cn("items-center px-8 pb-8 pt-6", {
            "relative z-20 pt-20": isHeaderAtFullWidth,
          })}
          gap="md"
        >
          {!isHeaderAtFullWidth && hasLoadedFeedData && (
            <Stack gap="md" className="w-[650px]">
              {shouldShowUnattributedReviewFilterCard && (
                <UnattributedReviewsFilterCard
                  isHeaderAtFullWidth={isHeaderAtFullWidth}
                  onFilterClick={handleToggleUnattributedReviews}
                  isActive={showUnattributedReviews}
                />
              )}
              <ActivityFeedHelpCard isHeaderAtFullWidth={isHeaderAtFullWidth} />
            </Stack>
          )}
          {showNoActivitiesMatchFilters ? (
            <Typography variant="bodyW1" textColor="secondary">
              No activities match your filters
            </Typography>
          ) : (
            activities.map((activity) => (
              <ActivityItem
                key={getActivityKey(activity)}
                activity={activity}
                onOpenEventModal={handleOpenEventModal}
                tglRule={tglRule}
              />
            ))
          )}
          {showInlineLoadingSkeleton && skeletonItems}
          {isFetchingNextPage && !showInlineLoadingSkeleton && skeletonItems}
          {hasNextPage && (
            <Button
              variant="secondary"
              onClick={handleLoadNextPage}
              disabled={isFetching || isFetchingNextPage}
              loading={isFetchingNextPage}
            >
              Load more
            </Button>
          )}
          {showUnattributedReviews && (
            <Button variant="ghost" onClick={handleToggleUnattributedReviews}>
              See all activity
            </Button>
          )}
        </Stack>
      </ScrollArea>

      <ActivityEventModal
        open={!!selectedActivity}
        onOpenChange={(open) => {
          if (!open) handleCloseEventModal();
        }}
        activity={selectedActivity}
      />
    </div>
  );
};

export default memo(ActivityFeed);
